import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/auth/useAuth';
import { FileUpload } from '../common/FileUpload';
import { supabase } from '../../lib/supabase';
import type { Job } from '../../types/job';

interface ApplicationFormProps {
  job: Job;
  onClose: () => void;
  onSuccess: () => void;
}

export function ApplicationForm({ job, onClose, onSuccess }: ApplicationFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.user_metadata?.first_name || '',
    lastName: user?.user_metadata?.last_name || '',
    email: user?.email || '',
    phone: '',
    coverLetter: '',
  });
  const [resumeUrl, setResumeUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!user) {
    toast.error('Please sign in to apply');
    return;
  }

  // Validate all required fields
  if (!formData.firstName.trim() || !formData.lastName.trim() ||
      !formData.email.trim() || !formData.phone.trim() ||
      !formData.coverLetter.trim() || !resumeUrl) {
    toast.error('All fields are required');
    return;
  }

  setIsSubmitting(true);
  try {
    const { data, error } = await supabase.functions.invoke('send-job-application', {
      body: {
        jobTitle: job.title,
        company: job.company,
        applicantName: `${formData.firstName} ${formData.lastName}`,
        applicantEmail: formData.email,
        applicantPhone: formData.phone,
        coverLetter: formData.coverLetter,
        resumeUrl: resumeUrl,
        jobId: job.id,
        userId: user.id
      }
    });

    if (error) throw error;
    
    toast.success('Application submitted successfully!');
    onSuccess();
    onClose();
  } catch (error) {
    console.error('Application error:', error);
    toast.error('Error submitting application. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black">
              Apply for {job.title} at {job.company}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="input w-full text-black"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Cover Letter
            </label>
            <textarea
              value={formData.coverLetter}
              onChange={e => setFormData(prev => ({ ...prev, coverLetter: e.target.value }))}
              className="w-full h-40 bg-white border border-gray-200 rounded-xl p-3 text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
              placeholder="Why are you a great fit for this role?"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Resume
            </label>
            <FileUpload
              bucket="resumes"
              path={`jobs/${user?.id}`}
              onUpload={(url) => setResumeUrl(url)}
              accept=".pdf,.doc,.docx"
            />
            <p className="mt-1 text-sm text-gray-500">
              Accepted format: PDF
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary flex items-center"
            >
              {isSubmitting ? (
                <>
                  <Upload className="animate-spin h-4 w-4 mr-2" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}